import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card } from '../../../components/ui/card';
import { Building2, Briefcase, UserCheck, Network, Users, AlertCircle, TrendingUp, Settings } from 'lucide-react';
import { omAPI } from '../../../services/api';

const OMDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    orgUnits: 0,
    jobs: 0,
    positions: 0,
    vacantPositions: 0,
    employees: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [orgUnitsRes, jobsRes, positionsRes, chartRes] = await Promise.all([
        omAPI.getOrgUnits(),
        omAPI.getJobs(),
        omAPI.getPositions(),
        omAPI.getOrgChart()
      ]);
      
      setStats({
        orgUnits: orgUnitsRes.data?.length || 0,
        jobs: jobsRes.data?.length || 0,
        positions: positionsRes.data?.length || 0,
        vacantPositions: chartRes.data?.vacant_positions || 0,
        employees: chartRes.data?.total_employees || 0
      });
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const modules = [
    {
      title: 'Org Units',
      description: 'Manage organizational units, departments, and divisions',
      icon: Building2,
      color: 'bg-orange-500',
      path: '/hcm/om/org-units',
      stat: stats.orgUnits,
      statLabel: 'Total Units'
    },
    {
      title: 'Jobs',
      description: 'Define job classifications and role templates',
      icon: Briefcase,
      color: 'bg-green-500',
      path: '/hcm/om/jobs',
      stat: stats.jobs,
      statLabel: 'Total Jobs'
    },
    {
      title: 'Positions',
      description: 'Manage positions and staffing assignments',
      icon: UserCheck,
      color: 'bg-orange-500',
      path: '/hcm/om/positions',
      stat: stats.positions,
      statLabel: 'Total Positions'
    },
    {
      title: 'Org Chart',
      description: 'View organizational structure and reporting lines',
      icon: Network,
      color: 'bg-orange-500',
      path: '/hcm/om/org-chart',
      stat: stats.vacantPositions,
      statLabel: 'Vacant Positions',
      statColor: stats.vacantPositions > 0 ? 'text-yellow-600' : 'text-gray-900'
    }
  ];

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="om-dashboard">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
            <span className="cursor-pointer hover:text-gray-700" onClick={() => navigate('/hcm')}>HCM</span>
            <span>/</span>
            <span>Organizational Management</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Organizational Management</h1>
          <p className="text-gray-600 mt-1">Manage your organizational structure, jobs, and positions</p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Building2 className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Org Units</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.orgUnits}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Briefcase className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Jobs</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.jobs}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <UserCheck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Positions</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.positions}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertCircle className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Vacant</p>
                <p className="text-xl font-semibold text-yellow-600">{loading ? '-' : stats.vacantPositions}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Users className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Assigned</p>
                <p className="text-xl font-semibold">{loading ? '-' : stats.employees}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Module Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {modules.map((module, idx) => (
            <Card 
              key={idx} 
              className="p-6 hover:shadow-lg transition-shadow cursor-pointer" 
              onClick={() => navigate(module.path)}
              data-testid={`om-module-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${module.color}`}>
                  <module.icon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">{module.title}</h2>
                      <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-2xl font-bold ${module.statColor || 'text-gray-900'}`}>
                        {loading ? '-' : module.stat}
                      </p>
                      <p className="text-xs text-gray-500">{module.statLabel}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button 
              onClick={() => navigate('/hcm/om/org-units')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <Building2 className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Create Org Unit</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/om/jobs')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <Briefcase className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium">Create Job</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/om/positions')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <UserCheck className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">Create Position</span>
            </button>
            <button 
              onClick={() => navigate('/hcm/om/org-chart')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors text-left"
            >
              <Network className="h-5 w-5 text-orange-600" />
              <span className="text-sm font-medium">View Org Chart</span>
            </button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default OMDashboard;
